/* RESPONSIBLE TEAM: team-standalone */
import type AppService from 'embercom/services/app-service';
import Service, { inject as service } from '@ember/service';
import type ReportingMetrics from 'embercom/services/reporting-metrics';
import type ReportingTemplates from 'embercom/services/reporting-templates';
import { type ChartTemplate, type ReportTemplate } from 'embercom/services/reporting-templates';
import type ChartSeries from 'embercom/models/reporting/custom/chart-series';
import { type Attribute } from 'embercom/objects/reporting/unified/datasets/types';
import { isPresent } from '@ember/utils';
import type Metric from 'embercom/objects/reporting/unified/metrics/types';
import { uniq } from 'underscore';

export const ALLOWED_REPORT_TEMPLATES = ['fin_ai_agent', 'conversations', 'ai_csat', 'other'];
const ALLOWED_METRIC_IDS_WITH_CUSTOM_ANSWERS = [
  'v1.new_conversations',
  'fin.conversations_resolved.count',
  'fin.conversations_answered.count',
  'fin.conversations_participated.count',
  'fin.participation_rate',
  'fin.resolution_rate',
  'fin.conversations_deflected.count',
  'fin.deflection_rate',
  'fin.conversations_abandoned.count',
  'fin.abandonment_rate',
  'fin.answer_rate',
  'fin.conversations_confirmed_resolved.count',
  'fin.conversations_assumed_resolved.count',
  'fin.conversations_routed_to_team.count',
  'fin.routed_to_team_rate',
  'fin.unanswered.count',
  'fin.unresolved_conversations.count',
  'fin.ai_answer.count',
  'fin.custom_answer.count',
  'fin.ai_answer.resolution_rate',
  'fin.custom_answer.resolution_rate',
  'conversation_rating.ai_agent.csat',
  'conversation_rating.ai_agent.positively_rated.count',
  'conversation_rating.ai_agent.negatively_rated.count',
  'conversation_rating.ai_agent.count',
  'conversation_rating.ai_agent.dsat',
  'conversation_custom_fields#customer_experience_attributes',
  'conversation.ai_generated_metrics.csat.count',
  'conversation.ai_generated_metrics.csat.count.first_user_conversation_part_created_at.ai_agent',
  'conversation.ai_generated_metrics.csat.positively_rated.count',
  'conversation.ai_generated_metrics.csat.positively_rated.count.first_user_conversation_part_created_at.ai_agent',
  'conversation.ai_generated_metrics.csat_score',
  'conversation.ai_generated_metrics.csat_score.first_user_conversation_part_created_at',
  'conversation.ai_generated_metrics.csat.count.first_user_conversation_part_created_at',
  'scorecard_evaluation.evaluated_count',
  'scorecard_evaluation.score',
  'scorecard_evaluation.passed_count',
  'scorecard_evaluation.failed_count',
  'scorecard_evaluation.pass_rate',
  'scorecard_evaluation.fail_rate',
  'scorecard_attribute_evaluation.evaluated_count',
  'scorecard_attribute_evaluation.passed_count',
  'scorecard_attribute_evaluation.failed_count',
  'scorecard_attribute_evaluation.score',
  'conversations.recontact_rate_24h',
  'conversations.recontact_rate_48h',
  'conversations.recontact_rate_72h',
];
export const ALLOWED_METRIC_IDS = ALLOWED_METRIC_IDS_WITH_CUSTOM_ANSWERS.filter(
  (metricId) => !metricId.startsWith('fin.custom_answer.'),
);

const ALLOWED_COPILOT_METRIC_IDS = [
  'copilot.teammate_searches.standalone',
  'copilot.teammate_using.standalone',
];

const ALLOWED_COPILOT_ATTRIBUTE_IDS = [
  'copilot.question.id',
  'copilot.question.asked_at',
  'copilot.content_references',
  'copilot.question.text',
  'copilot.answer.text',
  'copilot.response_part_status',
  'copilot.response_part_interaction_events',
  'copilot.conversation.admin_id', // whoever is created the copilot conversation part, not directly related to the conversation despite the name
];

export const ALLOWED_ATTRIBUTE_IDS = [
  'conversation.id',
  'conversation.first_user_conversation_part_created_at',
  'conversation.channel_type',
  'conversation.conversation_state',
  'conversation.user.location.continent_code',
  'conversation.user.location.country_code',
  'conversation.first_user.id',
  'conversation.first_user.name',
  'conversation.first_user.email',
  'conversation.conversation_tag_ids',
  'conversation.last_closed_at',
  'scorecard_evaluation.scorecard_score',
  'scorecard_evaluation.scorecard_outcome',
  'scorecard_evaluation.scorecard_id',
  'scorecard_evaluation.monitor_id',
  'scorecard_evaluation.scorecard_updated_at',
  'scorecard_evaluation.review_status',
  'scorecard_evaluation.reviewed_by_admin_id',
  'scorecard_evaluation.reviewed_admin_id',
  'scorecard_attribute_evaluation.scorecard_attribute_id',
  'scorecard_attribute_evaluation.score',
  'scorecard_attribute_evaluation.outcome',
  'scorecard_attribute_evaluation.value',
  'scorecard_attribute_evaluation.review_status',
  'scorecard_attribute_evaluation.reviewed_by_admin_id',
  'scorecard_attribute_evaluation.reviewed_admin_id',
  'conversation.companies.company_id',
  'conversation.companies.name',
  'conversation.companies.plan_id',
  'conversation.companies.size',
  'conversation.companies.manual_tag_ids',
];

export const ALLOWED_ATTRIBUTE_IDS_WITH_AI_CSAT = [
  ...ALLOWED_ATTRIBUTE_IDS,
  'conversation.ai_generated_metrics.csat',
  'conversation.ai_generated_metrics.csat_explanation',
];

export const ALLOWED_FILTERABLE_ATTRIBUTE_IDS = [
  'time',
  'conversation.channel_type',
  'conversation.conversation_state',
  'conversation.user.location.continent_code',
  'conversation.user.location.country_code',
  'conversation.first_user.name',
  'conversation.first_user.email',
  'conversation.conversation_tag_ids',
  'conversation.companies.company_id',
  'conversation.companies.name',
  'conversation.companies.plan_id',
  'conversation.companies.size',
  'conversation.companies.manual_tag_ids',
  'conversation.fin.guidelines_applied.guideline_ids',
  'conversation.fin.procedures_triggered.procedure_ids',
  'conversation.fin.procedures_failed.procedure_ids',
  'conversation.fin.escalation_rules_applied.escalation_rule_ids',
  'conversation.fin.participated',
];

export const ALLOWED_FILTERABLE_ATTRIBUTE_IDS_WITH_AI_CSAT = [
  ...ALLOWED_FILTERABLE_ATTRIBUTE_IDS,
  'conversation.ai_generated_metrics.csat',
  'conversation.ai_generated_metrics.csat_explanation',
];

export const ALLOWED_FIN_ATTRIBUTE_IDS = [
  'conversation.replied.fin_ai_agent',
  'conversation.fin.last_sent_answer_type',
  'conversation.fin.resolution_state',
  'conversation.fin.deflected',
  'conversation.fin.participated',
  'conversation.fin.content_references',
  'conversation.fin.knowledge_hub_content_references',
  'conversation.fin.guidelines_applied.guideline_ids',
  'conversation.fin.escalation_rules_applied.escalation_rule_ids',
  'conversation.fin.procedures_triggered.procedure_ids',
  'conversation.fin.procedures_failed.procedure_ids',
];

export const ALLOWED_FIN_CSAT_ATTRIBUTE_IDS = [
  'conversation.bot_conversation_rating.updated_at',
  'conversation.bot_conversation_rating.rating_index',
  'conversation.bot_conversation_rating.remark',
  'conversation_rating_sent.updated_at',
  'conversation_rating_sent.rating_index',
  'conversation_rating_sent.rating_participants.fin',
  'conversation_rating_sent.rated_actor_type',
  'conversation_rating_sent.last_survey_sent_conversation_part_id',
  'conversation_rating_sent.remark',
];

const TIME_TO_CLOSE_METRIC_IDS = [
  'v2.time_to_close.bot_inbox_included',
  'v2.time_to_close.bot_inbox_included.in_office_hours',
  'time_to_first_human_close.bot_inbox_included',
  'time_to_first_human_close.bot_inbox_included.in_office_hours',
];

export const HIDDEN_FEATURES = ['showReportOwner', 'showReportAction'];

export const ALLOWED_CDA_ATTRIBUTES_GROUPS = [
  'conversation_custom_fields',
  'user.custom_data',
  'companies.custom_data',
];

export default class ReportingStandalone extends Service {
  @service declare reportingMetrics: ReportingMetrics;
  @service declare reportingTemplates: ReportingTemplates;
  @service declare appService: AppService;

  filterReportTemplates(reportTemplates: ReportTemplate[]) {
    return reportTemplates.filter((template: ReportTemplate) =>
      ALLOWED_REPORT_TEMPLATES.includes(template.templateId),
    );
  }

  filterStandaloneCharts(reportTemplates: any[]) {
    let fields = uniq(
      this.supportedDatasetIds
        .map((datasetId: string) =>
          this.getAttributesForStandalone(datasetId).map((attribute: Attribute) => attribute.field),
        )
        .flat(),
    );

    let result = reportTemplates.reduce((filteredTemplates: any[], reportTemplate: any) => {
      if (ALLOWED_REPORT_TEMPLATES.includes(reportTemplate.reportId)) {
        let chartTemplates = reportTemplate.chartTemplates.filter((chart: ChartTemplate) => {
          let includesOnlyAllowedMetrics = chart.chartSeries.every((series: ChartSeries) =>
            this.allowedMetricIds.includes(series.metricId),
          );
          let viewByIsAllowed =
            !isPresent(chart.viewBy) || chart.viewBy === 'time' || fields.includes(chart.viewBy);
          let segmentByIsAllowed =
            !isPresent(chart.segmentBy) ||
            chart.segmentBy === 'time' ||
            fields.includes(chart.segmentBy);
          return includesOnlyAllowedMetrics && viewByIsAllowed && segmentByIsAllowed;
        });

        if (chartTemplates.length > 0) {
          filteredTemplates.push({
            ...reportTemplate,
            chartTemplates,
          });
        }
      }

      return filteredTemplates;
    }, []);

    let componentTemplates = reportTemplates.find(
      (reportTemplate: any) => reportTemplate.reportId === 'component',
    );
    if (componentTemplates) {
      return [componentTemplates, ...result];
    }
    return result;
  }

  private get supportedDatasetIds() {
    let defaultDatasetIds = ['conversation'];

    if (this.appService.app?.canUseStandaloneCopilotReporting) {
      return [...defaultDatasetIds, 'standalone_copilot_prompt_response_pair'];
    }

    return defaultDatasetIds;
  }

  getAttributesForStandalone(datasetId: string) {
    let attributes = this.reportingMetrics.getDatasetById(datasetId).attributes;
    let allowedAttributeIds = this.getAllowedAttributeIds();
    let standardAttributes = attributes.filter((attribute: Attribute) =>
      allowedAttributeIds.includes(attribute.id),
    );
    let cdaAttributes = attributes.filter((attribute: Attribute) =>
      ALLOWED_CDA_ATTRIBUTES_GROUPS.includes(attribute.group!),
    );

    let finAttributes = attributes.filter(
      (attribute: Attribute) =>
        attribute.group === 'fin' && ALLOWED_FIN_ATTRIBUTE_IDS.includes(attribute.id),
    );

    let result = [...standardAttributes, ...cdaAttributes, ...finAttributes];
    let finCSATAttributesIds = attributes.filter((attribute: Attribute) =>
      ALLOWED_FIN_CSAT_ATTRIBUTE_IDS.includes(attribute.id),
    );
    result = result.concat(finCSATAttributesIds);
    return result.compact();
  }

  supportsFeature(featureName: string) {
    return !HIDDEN_FEATURES.includes(featureName);
  }

  filterMetrics(metrics: Metric[]) {
    return metrics.filter((metric: Metric) => this.allowedMetricIds.includes(metric.id));
  }

  filterSuggestedAttributes(attributeIds: string[]) {
    let allowedAttributeIds = this.getAllowedAttributeIds();
    let attributes = [
      ...allowedAttributeIds,
      ...ALLOWED_FIN_ATTRIBUTE_IDS,
      ...ALLOWED_FIN_CSAT_ATTRIBUTE_IDS,
    ];

    return attributeIds.filter((attributeId: string) => attributes.includes(attributeId));
  }

  private getAllowedAttributeIds() {
    let allowedAttributeIds = this.appService.app?.canSeeAiCsatMetrics
      ? ALLOWED_ATTRIBUTE_IDS_WITH_AI_CSAT
      : ALLOWED_ATTRIBUTE_IDS;

    if (this.appService.app?.canSeeAiCsatMetrics) {
      allowedAttributeIds = [...allowedAttributeIds, 'conversation.agent_types_involved_grouping'];
    }

    if (this.appService.app?.canUseStandaloneCopilotReporting) {
      allowedAttributeIds = [...allowedAttributeIds, ...ALLOWED_COPILOT_ATTRIBUTE_IDS];
    }

    return allowedAttributeIds;
  }

  private get allowedMetricIds() {
    let metrics = this.appService.app.canUseFeature('team-standalone-custom-answers')
      ? ALLOWED_METRIC_IDS_WITH_CUSTOM_ANSWERS
      : ALLOWED_METRIC_IDS;

    if (this.appService.app?.canUseStandaloneCopilotReporting) {
      metrics = metrics.concat(ALLOWED_COPILOT_METRIC_IDS);
    }

    if (this.appService.app.canUseFeature('team-standalone-time-to-close-metrics')) {
      metrics = metrics.concat(TIME_TO_CLOSE_METRIC_IDS);
    }

    return metrics;
  }
}

// Exported functions for components to use instead of static constants
export function getAllowedAttributeIdsForStandalone(appService: any): string[] {
  let allowedAttributeIds = appService?.app?.canSeeAiCsatMetrics
    ? ALLOWED_ATTRIBUTE_IDS_WITH_AI_CSAT
    : ALLOWED_ATTRIBUTE_IDS;

  if (appService?.app?.canSeeAiCsatMetrics) {
    allowedAttributeIds = [...allowedAttributeIds, 'conversation.agent_types_involved_grouping'];
  }

  return allowedAttributeIds;
}

export function getAllowedFilterableAttributeIdsForStandalone(appService: any): string[] {
  let allowedAttributeIds = appService?.app?.canSeeAiCsatMetrics
    ? ALLOWED_FILTERABLE_ATTRIBUTE_IDS_WITH_AI_CSAT
    : ALLOWED_FILTERABLE_ATTRIBUTE_IDS;

  if (appService?.app?.canSeeAiCsatMetrics) {
    allowedAttributeIds = [...allowedAttributeIds, 'conversation.agent_types_involved_grouping'];
  }

  if (appService?.app?.canUseFeature('fin-releases')) {
    allowedAttributeIds = [...allowedAttributeIds, 'conversation.releases.release_ids'];
  }

  return allowedAttributeIds;
}
